import type { SmmPackage, SmmProductConfig, SmmTierConfig } from '#bv/data/products'

export type { SmmTierConfig, SmmTierId } from '#bv/data/products'

export function smmHasTiers(cfg: SmmProductConfig): boolean {
  return Boolean(cfg.tiers?.length)
}

export function listSmmTiers(cfg: SmmProductConfig): SmmTierConfig[] {
  if (cfg.tiers?.length) return cfg.tiers
  return []
}

export function defaultSmmTierId(cfg: SmmProductConfig): string {
  if (cfg.tiers?.length) {
    const preferred = cfg.defaultTierId
    if (preferred && cfg.tiers.some((t) => t.id === preferred)) return preferred
    return cfg.tiers[0]!.id
  }
  return 'elite'
}

export function getSmmTier(cfg: SmmProductConfig, tierId: string): SmmTierConfig | null {
  return cfg.tiers?.find((t) => t.id === tierId) ?? null
}

/** Effective config for fulfillment / validation (tier overrides packages + picker). */
export function resolveSmmConfigForTier(cfg: SmmProductConfig, tierId: string): SmmProductConfig {
  const tier = getSmmTier(cfg, tierId)
  if (!tier) return cfg
  return {
    ...cfg,
    tierLabel: tier.label,
    servicePicker: tier.servicePicker,
    packages: tier.packages,
    trustBadges: tier.trustBadges,
    startTime: tier.startTime ?? cfg.startTime,
    deliverySpeed: tier.deliverySpeed ?? cfg.deliverySpeed,
    refillDays: tier.refillDays,
    dripFeed: tier.dripFeed ?? cfg.dripFeed,
    dropRate: tier.dropRate,
  }
}

export function allSmmPackages(cfg: SmmProductConfig): SmmPackage[] {
  if (cfg.tiers?.length) {
    return cfg.tiers.flatMap((t) => t.packages)
  }
  return cfg.packages
}

export function minSmmPackagePrice(cfg: SmmProductConfig): number {
  const pkgs = allSmmPackages(cfg)
  if (!pkgs.length) return 0
  return Math.min(...pkgs.map((p) => p.price))
}

export function findSmmPackage(
  cfg: SmmProductConfig,
  tierId: string | undefined,
  packageQuantity: number,
): SmmPackage | undefined {
  const tid = tierId?.trim() || defaultSmmTierId(cfg)
  const resolved = smmHasTiers(cfg) ? resolveSmmConfigForTier(cfg, tid) : cfg
  return resolved.packages.find((p) => p.quantity === packageQuantity)
}

export function inferSmmTierIdFromOrder(
  cfg: SmmProductConfig,
  productName: string,
  packageLabel: string,
): string {
  if (!cfg.tiers?.length) return defaultSmmTierId(cfg)
  const hay = `${productName} ${packageLabel}`.toLowerCase()
  for (const t of cfg.tiers) {
    if (hay.includes(t.label.toLowerCase())) return t.id
  }
  return defaultSmmTierId(cfg)
}

/** Scale elite retail packages for lower tiers. */
export function scaleSmmPackages(
  packages: SmmPackage[],
  multiplier: number,
): SmmPackage[] {
  return packages.map((p) => ({
    ...p,
    price: Math.max(0.99, Math.round(p.price * multiplier * 100) / 100),
  }))
}

const SHARED_EXCLUDE = ['bot', 'fake', 'autolike', 'likes only', 'views only', 'comment only']

export function buildFollowerTiers(input: {
  elitePackages: SmmPackage[]
  elitePicker: NonNullable<SmmProductConfig['servicePicker']>
  platformKeywords: { name: string[]; category: string[]; extraExclude?: string[] }
  standardMultiplier?: number
  budgetMultiplier?: number
  eliteTrust: SmmProductConfig['trustBadges']
  standardTrust: SmmProductConfig['trustBadges']
  budgetTrust: SmmProductConfig['trustBadges']
  startTime: string
  deliverySpeed: string
  dripFeed?: boolean
}): SmmTierConfig[] {
  const exclude = [...SHARED_EXCLUDE, ...(input.platformKeywords.extraExclude ?? [])]
  const panels = input.elitePicker.panels ?? ['morethanpanel', 'skaiyoh']
  const stdMul = input.standardMultiplier ?? 0.72
  const budMul = input.budgetMultiplier ?? 0.48

  return [
    {
      id: 'elite',
      label: 'Elite',
      blurb: 'Lowest drop · 30-day refill · premium services',
      dropRate: 'Low',
      refillDays: 30,
      dripFeed: input.dripFeed,
      startTime: input.startTime,
      deliverySpeed: input.deliverySpeed,
      servicePicker: {
        ...input.elitePicker,
        mode: 'cheapest_rules',
        panels,
        requireRefill: true,
        nameKeywords: input.platformKeywords.name,
        categoryKeywords: input.platformKeywords.category,
        excludeNameKeywords: exclude,
        qualityNameKeywords: ['real', 'premium', 'hq', 'high quality', 'active', 'refill'],
      },
      packages: input.elitePackages,
      trustBadges: input.eliteTrust,
    },
    {
      id: 'standard',
      label: 'Standard',
      blurb: 'Balanced price · moderate drop · refill on eligible services',
      dropRate: 'Medium',
      refillDays: 15,
      dripFeed: input.dripFeed,
      startTime: input.startTime,
      deliverySpeed: input.deliverySpeed,
      servicePicker: {
        mode: 'cheapest_rules',
        panels,
        requireRefill: true,
        nameKeywords: input.platformKeywords.name,
        categoryKeywords: input.platformKeywords.category,
        excludeNameKeywords: exclude,
      },
      packages: scaleSmmPackages(input.elitePackages, stdMul),
      trustBadges: input.standardTrust,
    },
    {
      id: 'budget',
      label: 'Budget',
      blurb: 'Lowest price · higher drop · best for testing',
      dropRate: 'Higher',
      refillDays: 0,
      dripFeed: input.dripFeed ?? false,
      startTime: input.startTime,
      deliverySpeed: input.deliverySpeed,
      servicePicker: {
        mode: 'cheapest_rules',
        panels,
        requireRefill: false,
        nameKeywords: input.platformKeywords.name,
        categoryKeywords: input.platformKeywords.category,
        excludeNameKeywords: exclude,
      },
      packages: scaleSmmPackages(input.elitePackages, budMul),
      trustBadges: input.budgetTrust,
    },
  ]
}
